<template>
  <ul class="flex flex-wrap gap-2">
    <li 
      v-for="optionValue in options" 
      :key="optionValue.id"
      :title="optionValue.name"
      class="relative size-8 rounded-full cursor-pointer transition-all duration-200"
      :class="{
        'ring-2 ring-offset-2 ring-primary': selected === optionValue.id,
        'hover:ring-2 hover:ring-offset-1 hover:ring-muted': selected !== optionValue.id && optionValue.available,
        'cursor-not-allowed': !optionValue.available
      }"
      @click="optionValue.available && selectOption(optionValue.id)"
    >
      <!-- Color swatch -->
      <span 
        class="absolute inset-0 rounded-full border border-gray-200"
        :class="{
          'opacity-40': !optionValue.available
        }"
        :style="{ backgroundColor: optionValue.hex }"
      />
      
      <!-- Strikethrough for unavailable -->
      <span 
        v-if="!optionValue.available" 
        class="absolute inset-0 flex items-center justify-center"
      >
        <span class="w-full h-0.5 bg-red-400 rotate-45 rounded-full" />
      </span>
      
      <!-- Checkmark for selected -->
      <span 
        v-if="selected === optionValue.id" 
        class="absolute inset-0 flex items-center justify-center"
      >
        <UIcon 
          name="i-lucide-check" 
          class="size-4"
          :class="isLightColor(optionValue.hex) ? 'text-gray-800' : 'text-white'"
        />
      </span>
    </li>
  </ul>
</template>
<script setup lang="ts">
import type { ProductOptionValueWithAvailability } from '~/types';

const {
    options,
    selected,
} = defineProps<{
  options: ProductOptionValueWithAvailability[],
  selected?: number
}>()

const emit = defineEmits<{
  (e: 'selectOption', optionValueId: number): void
}>()

function selectOption(valueId: number) {
    emit('selectOption', valueId)
}

const isLightColor = (hexColor?: string): boolean => {
    if (!hexColor) return false;
    // Remove the hash if it's there
    const hex = hexColor.replace('#', '');

    // Convert 3-digit hex to 6-digit hex
    const fullHex = hex.length === 3 
        ? hex.split('').map(c => c + c).join('') 
        : hex;

    const r = parseInt(fullHex.substring(0, 2), 16);
    const g = parseInt(fullHex.substring(2, 4), 16);
    const b = parseInt(fullHex.substring(4, 6), 16);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance > 0.5; // Threshold for light color
}
</script>